import { isDefined } from '@sgme/fp';
import clsx from 'clsx';
import type { FocusEvent, KeyboardEvent } from 'react';

import { useSgCurrencyPairPickerContext } from './context.tsx';
import { actions } from './reducer.ts';

export type InputProps = {
  'data-e2e'?: string;
};

export const CurrencyPairInput = (props: InputProps) => {
  const { 'data-e2e': dataE2e } = props;

  const {
    state: { hasError, value, editedValue, filteredCurrencyPairs, allCurrencyPairs },
    dispatch,
    comboBox,
    inputRef,
    setInputRef,
    onChange,
  } = useSgCurrencyPairPickerContext();

  const { getInputProps, isOpen, highlightedIndex, selectItem, closeMenu } = comboBox;

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    dispatch(actions.inputFocused());

    event.target.select();
  };

  const handleBlur = (_event: FocusEvent<HTMLInputElement>) => {
    dispatch(actions.inputBlurred());

    // If the field is empty after blur, call onChange with undefined
    if (editedValue === '') {
      onChange?.(undefined);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Let Downshift handle arrow keys first, then our custom logic
    if (event.key === 'Enter' || event.key === 'Tab') {
      // Check if there are selectable currency pairs
      if (isOpen && filteredCurrencyPairs.length > 0) {
        // If the typed value is an exact match, prefer it over the first filtered item
        // (e.g. typing 'USD/EUR' should select 'USD/EUR', not 'EUR/USD')
        const exactMatch = allCurrencyPairs.includes(editedValue) ? editedValue : undefined;
        const itemToSelect = exactMatch ?? filteredCurrencyPairs[highlightedIndex >= 0 ? highlightedIndex : 0];

        selectItem(itemToSelect ?? null);

        if (isDefined(itemToSelect)) {
          dispatch(actions.currencyPairSelected({ currencyPair: itemToSelect }));
        }

        closeMenu();
        event.preventDefault(); // Prevent default Tab behavior

        // Remove focus from input
        inputRef?.blur();
      } else {
        closeMenu();
        event.preventDefault();
        inputRef?.blur();
      }
    } else if (event.key === 'Escape') {
      dispatch(actions.valueReset());
      closeMenu();
      event.preventDefault();
      inputRef?.blur();
    }
    // Note: Arrow keys are handled automatically by Downshift
  };

  return (
    <input
      {...getInputProps({
        placeholder: 'EUR/USD',
        ref: (node: HTMLInputElement | null) => {
          setInputRef(node);
        },
        onKeyDown,
      })}
      onBlur={handleBlur}
      onFocus={handleFocus}
      type="text"
      className={clsx('fw-medium text-center text-uppercase form-control px-4px', {
        'is-invalid': hasError,
      })}
      style={{ width: '82px' }}
      autoComplete="off"
      data-e2e={dataE2e}
      data-e2e-status={hasError ? 'error' : value === '' ? 'empty' : 'valid'}
    />
  );
};
